// БЫСТРЫЙ ЗАПУСК — СЛОВАРИ (ТЗ ~/projects/QUICK-START-TZ.md, Часть 3): типы полезняшки (C3), типы продукта (D6),
// восемь групп и 33 типа сообщений серии (§3.4) с медиа, кнопкой, подстрокой экрана галочек и заготовкой текста.
//
// INVARIANT(scriptera): тексты клиенту — ТОЛЬКО из ТЗ (ru дословно), en — перевод в том же строгом тоне: на «вы»,
// без восклицаний, без эмодзи, без рода у глаголов от лица автора. Новый тип/текст — только по слову владельца.
// Причина: владелец 27.10.2026 «везде строгая, нормальная, формальная речь». Канон: QUICK-START-TZ §2.0 п.7, §3.0.
//
// Файл чистый (без сети и базы): его читают сервер, страницы приложения и веб-форма.

export type QsLocale = 'ru' | 'en';
export type QsText = Record<QsLocale, string>;

// ── C3 «Полезняшка» ─────────────────────────────────────────────────────────────────────────────────────────────────

export const QS_GIFT_TYPES = ['guide', 'checklist', 'video', 'promo', 'link', 'custom'] as const;
export type QsGiftType = (typeof QS_GIFT_TYPES)[number];

/** Пункт списка на экране (заглавная) и слово в тексте (именительный, строчная). */
export const QS_GIFT_WORDS: Record<Exclude<QsGiftType, 'custom'>, { item: QsText; word: QsText }> = {
  guide: { item: { ru: 'Гайд', en: 'Guide' }, word: { ru: 'гайд', en: 'guide' } },
  checklist: { item: { ru: 'Чек-лист', en: 'Checklist' }, word: { ru: 'чек-лист', en: 'checklist' } },
  video: { item: { ru: 'Видео', en: 'Video' }, word: { ru: 'видео', en: 'video' } },
  promo: { item: { ru: 'Промокод', en: 'Promo code' }, word: { ru: 'промокод', en: 'promo code' } },
  link: { item: { ru: 'Ссылка', en: 'Link' }, word: { ru: 'ссылка', en: 'link' } },
};
export const QS_CUSTOM_ITEM: QsText = { ru: 'Своё', en: 'Other' };

// ── D6 «Что продаёте» ───────────────────────────────────────────────────────────────────────────────────────────────

export const QS_PRODUCT_TYPES = ['course', 'consult', 'club', 'webinar', 'service', 'goods', 'custom'] as const;
export type QsProductType = (typeof QS_PRODUCT_TYPES)[number];

export const QS_PRODUCT_WORDS: Record<Exclude<QsProductType, 'custom'>, { item: QsText; word: QsText }> = {
  course: { item: { ru: 'Курс', en: 'Course' }, word: { ru: 'курс', en: 'course' } },
  consult: { item: { ru: 'Консультация', en: 'Consultation' }, word: { ru: 'консультация', en: 'consultation' } },
  club: { item: { ru: 'Клуб по подписке', en: 'Subscription club' }, word: { ru: 'клуб по подписке', en: 'subscription club' } },
  webinar: { item: { ru: 'Вебинар или интенсив', en: 'Webinar or intensive' }, word: { ru: 'интенсив', en: 'intensive' } },
  service: { item: { ru: 'Услуга', en: 'Service' }, word: { ru: 'услуга', en: 'service' } },
  goods: { item: { ru: 'Товар', en: 'Product' }, word: { ru: 'товар', en: 'product' } },
};

// ── §3.4 Группы и типы сообщений серии ──────────────────────────────────────────────────────────────────────────────

export const QS_GROUPS = ['trust', 'proof', 'value', 'pain', 'engage', 'events', 'sale', 'return'] as const;
export type QsGroup = (typeof QS_GROUPS)[number];

export const QS_GROUP_TEXTS: Record<QsGroup, { title: QsText; intro: QsText }> = {
  trust: {
    title: { ru: 'Доверие', en: 'Trust' },
    intro: {
      ru: 'Покупают у того, кому доверяют. Два-три сообщения о вас делают серию разговором, а не рекламой.',
      en: 'People buy from those they trust. Two or three messages about you turn the series into a conversation rather than an advertisement.',
    },
  },
  proof: {
    title: { ru: 'Доказательства', en: 'Proof' },
    intro: {
      ru: 'Результат другого человека убеждает сильнее ваших слов. Покажите, что получилось у других.',
      en: 'Another person’s result is more convincing than your words. Show what others have achieved.',
    },
  },
  value: {
    title: { ru: 'Польза', en: 'Value' },
    intro: {
      ru: 'Дайте полезное бесплатно. Человек увидит, как вы объясняете, и захочет большего.',
      en: 'Give something useful for free. The person will see how you explain things and will want more.',
    },
  },
  pain: {
    title: { ru: 'Боль и будущее', en: 'Pain and the future' },
    intro: {
      ru: 'Назовите проблему словами самого человека и покажите, что будет, если ничего не менять.',
      en: 'Name the problem in the person’s own words and show what happens if nothing changes.',
    },
  },
  engage: {
    title: { ru: 'Вовлечение', en: 'Engagement' },
    intro: {
      ru: 'Дайте человеку повод ответить. Кто отвечает, тот читает дальше.',
      en: 'Give the person a reason to reply. Those who reply keep reading.',
    },
  },
  events: {
    title: { ru: 'События', en: 'Events' },
    intro: {
      ru: 'Бесплатная встреча — лучший способ показать себя вживую. Пригласите на вебинар или разбор.',
      en: 'A free session is the best way to present yourself live. Invite people to a webinar or a review.',
    },
  },
  sale: {
    title: { ru: 'Продажа', en: 'Sale' },
    intro: {
      ru: 'Теперь о продукте: что внутри, сколько стоит и почему сейчас.',
      en: 'Now about the product: what is inside, how much it costs and why now.',
    },
  },
  return: {
    title: { ru: 'Возврат', en: 'Return' },
    intro: {
      ru: 'Тем, кто перестал отвечать, — одно спокойное сообщение. Без давления.',
      en: 'One calm message for those who stopped replying. No pressure.',
    },
  },
};

/** Медиа, которое просится к сообщению (вкладки окна «Сообщение»). Значок строки D17 — те же ключи (§2.15 п.6). */
export type QsMedia = 'text' | 'photo' | 'video' | 'circle' | 'audio' | 'file';

/** Куда ведёт кнопка: ссылка «Куда ведёт кнопка» (D7) или ссылка события (D14, пусто — D7). */
export type QsButtonTarget = 'buy' | 'event';

export interface QsTypeDef {
  id: string;
  group: QsGroup;
  media: QsMedia;
  button?: { label: QsText; target: QsButtonTarget };
  title: QsText;
  /** Подстрока экрана галочек: «что это». */
  sub: QsText;
  /** Заготовка текста (§3.0: подстановки и необязательные отрезки `[[ ]]`). */
  text: QsText;
}

const BTN = {
  signup: { ru: 'Записаться', en: 'Sign up' },
  open: { ru: 'Открыть', en: 'Open' },
  more: { ru: 'Подробнее', en: 'Learn more' },
  buy: { ru: 'Купить', en: 'Buy' },
  wait: { ru: 'В лист ожидания', en: 'Join the waitlist' },
} as const;

/** §11.35 ч.1: серия «Продажи» — ровно одно сообщение с предложением. */
export const QS_SALES_TYPES: readonly string[] = ['offerMsg'];

export const QS_TYPES: readonly QsTypeDef[] = [
  // ── Доверие
  {
    id: 'intro',
    group: 'trust',
    media: 'text',
    button: { label: { ru: 'Подробнее', en: 'Learn more' }, target: 'buy' },
    title: { ru: 'Знакомство', en: 'Introduction' },
    sub: { ru: 'кто вы и почему вам можно доверять', en: 'who you are and why you can be trusted' },
    text: {
      ru: 'Здравствуйте. {offer} В ближайшие дни вы получите несколько коротких сообщений: что помогает достичь результата, что мешает и что представляет собой {product}. На любое сообщение можно ответить — я читаю ответы.',
      en: 'Hello. {offer} Over the next few days you will receive several short messages: what helps to achieve the result, what gets in the way and what the {product} is. You can reply to any message — I read the replies.',
    },
  },
  {
    id: 'story',
    group: 'trust',
    media: 'photo',
    button: { label: { ru: 'Читать дальше', en: 'Read on' }, target: 'buy' },
    title: { ru: 'Личная история', en: 'Personal story' },
    sub: { ru: 'как вы пришли к этому делу', en: 'how you came to this work' },
    text: {
      ru: 'Расскажу, как начался мой путь в этом деле. [[Если вы {evenIf}, мне это знакомо: в начале было так же.]] Большую часть ошибок можно было не совершать. Именно поэтому существует {product}[[ — чтобы вы прошли этот путь за {term}]].',
      en: 'Let me tell you how my path in this work began. [[If you {evenIf}, that is familiar to me: it was the same at the start.]] Most of the mistakes could have been avoided. That is exactly why the {product} exists[[ — so that you can cover this path in {term}]].',
    },
  },
  {
    id: 'backstage',
    group: 'trust',
    media: 'video',
    button: { label: { ru: 'Посмотреть', en: 'Take a look' }, target: 'buy' },
    title: { ru: 'Закулисье', en: 'Behind the scenes' },
    sub: { ru: 'как проходит ваш рабочий день', en: 'what your working day looks like' },
    text: {
      ru: 'Так проходит мой обычный рабочий день. Показываю его, потому что {productName} — не теория, а то, чем я занимаюсь ежедневно.',
      en: 'This is what my usual working day looks like. I am showing it because {productName} is not theory but what I do every day.',
    },
  },
  {
    id: 'numbers',
    group: 'trust',
    media: 'text',
    button: { label: { ru: 'Подробнее', en: 'Learn more' }, target: 'buy' },
    title: { ru: 'Цифры', en: 'Numbers' },
    sub: { ru: 'сколько учеников, лет практики, результатов', en: 'how many students, years of practice, results' },
    text: {
      ru: 'Несколько цифр вместо слов: … человек прошли этот путь вместе со мной, … лет практики, … результатов. Честные числа убеждают лучше обещаний.',
      en: 'A few numbers instead of words: … people have taken this path with me, … years of practice, … results. Honest numbers are more convincing than promises.',
    },
  },
  // ── Доказательства
  {
    id: 'case',
    group: 'proof',
    media: 'photo',
    button: { label: { ru: 'Читать кейс', en: 'Read the case' }, target: 'buy' },
    title: { ru: 'Кейс ученика', en: 'Student case' },
    sub: { ru: 'было и стало у одного человека', en: 'before and after for one person' },
    text: {
      ru: 'История одного ученика. [[В начале — как у многих: {evenIf}.]] Результат: удалось {result}[[ за {term}]]. [[Что сработало: {how}.]] Таких историй много, и ваша может стать следующей.',
      en: 'The story of one student. [[At the start it was the same as for many: they {evenIf}.]] The result: they managed {result}[[ in {term}]]. [[What worked: {how}.]] There are many such stories, and yours may be the next one.',
    },
  },
  {
    id: 'review',
    group: 'proof',
    media: 'circle',
    button: { label: { ru: 'Смотреть отзыв', en: 'See the review' }, target: 'buy' },
    title: { ru: 'Отзыв', en: 'Review' },
    sub: { ru: 'снимок переписки или видеоотзыв', en: 'a chat screenshot or a video review' },
    text: {
      ru: 'Вместо пересказа — отзыв участника {productName}. Посмотрите, что пишут после прохождения.',
      en: 'Instead of a retelling — a review from a participant of {productName}. See what people write after completing it.',
    },
  },
  {
    id: 'compare',
    group: 'proof',
    media: 'text',
    button: { label: { ru: 'Сравнить', en: 'Compare' }, target: 'buy' },
    title: { ru: 'Сравнение', en: 'Comparison' },
    sub: { ru: 'самостоятельно или с наставником', en: 'on your own or with a mentor' },
    text: {
      ru: 'Разобраться самостоятельно возможно, и у многих получается. Но самостоятельный путь обычно занимает год проб и ошибок[[, а путь по системе — {term}]]. [[Система — это {how}.]] Разница не в усилиях, а в маршруте.',
      en: 'It is possible to figure it out on your own, and many succeed. But the independent path usually takes a year of trial and error[[, while the path with a system takes {term}]]. [[The system is {how}.]] The difference is not in the effort but in the route.',
    },
  },
  // ── Польза
  {
    id: 'lesson',
    group: 'value',
    media: 'text',
    button: { label: { ru: 'Смотреть урок', en: 'Watch the lesson' }, target: 'buy' },
    title: { ru: 'Мини-урок', en: 'Mini lesson' },
    sub: { ru: 'три совета, которые можно применить сегодня', en: 'three tips you can apply today' },
    text: {
      ru: 'Три действия, которые можно выполнить уже сегодня[[, чтобы {result}]]: 1) … 2) … 3) … Попробуйте и напишите, что получилось.',
      en: 'Three actions you can take today[[ {result}]]: 1) … 2) … 3) … Try them and write what happened.',
    },
  },
  {
    id: 'podcast',
    group: 'value',
    media: 'audio',
    button: { label: { ru: 'Слушать', en: 'Listen' }, target: 'buy' },
    title: { ru: 'Подкаст', en: 'Podcast' },
    sub: { ru: 'аудиозапись на 15–20 минут', en: 'a 15–20 minute audio recording' },
    text: {
      ru: 'Для вас записано аудио на 20 минут[[: почему не удаётся {result} и что с этим делать]]. Удобно слушать в дороге.',
      en: 'A 20-minute audio has been recorded for you[[: why it is hard {result} and what to do about it]]. It is convenient to listen to on the way.',
    },
  },
  {
    id: 'video',
    group: 'value',
    media: 'video',
    button: { label: { ru: 'Смотреть', en: 'Watch' }, target: 'buy' },
    title: { ru: 'Видеоразбор', en: 'Video review' },
    sub: { ru: 'урок или разбор на видео', en: 'a lesson or a review on video' },
    text: {
      ru: 'Короткий видеоразбор[[: на примере показано, как работает {how}]].',
      en: 'A short video review[[: an example shows how {how} works]].',
    },
  },
  {
    id: 'circle',
    group: 'value',
    media: 'circle',
    button: { label: { ru: 'Смотреть', en: 'Watch' }, target: 'buy' },
    title: { ru: 'Кружок с мыслью', en: 'Video note with a thought' },
    sub: { ru: 'одна мысль за минуту', en: 'one thought in a minute' },
    text: {
      ru: 'Одна мысль на сегодня — в видеосообщении. Одна минута, но важная.',
      en: 'One thought for today — in a video message. One minute, but an important one.',
    },
  },
  {
    id: 'mistakes',
    group: 'value',
    media: 'text',
    button: { label: { ru: 'Читать разбор', en: 'Read the breakdown' }, target: 'buy' },
    title: { ru: 'Разбор ошибок', en: 'Common mistakes' },
    sub: { ru: 'типичные ошибки и как их избежать', en: 'typical mistakes and how to avoid them' },
    text: {
      ru: 'Три ошибки, которые совершают почти все[[, кто хочет {result}]]: 1) … 2) … 3) … Если вы узнали себя — это поправимо, просто вам никто не показал, как действовать иначе.',
      en: 'Three mistakes almost everyone makes[[ when trying {result}]]: 1) … 2) … 3) … If you recognised yourself, it can be fixed — nobody has simply shown you how to act differently.',
    },
  },
  {
    id: 'myth',
    group: 'value',
    media: 'text',
    button: { label: { ru: 'Узнать правду', en: 'Learn the truth' }, target: 'buy' },
    title: { ru: 'Миф и правда', en: 'Myth and truth' },
    sub: { ru: 'заблуждение, которое мешает', en: 'a misconception that gets in the way' },
    text: {
      ru: 'Распространённое мнение: [[чтобы {result}, ]]нужны годы и особый талант. На практике нужна система[[ — {how}]]. Талант не обязателен[[, срок — {term}]].',
      en: 'A common belief: [[in order {result}, ]]you need years and a special talent. In practice you need a system[[ — {how}]]. Talent is not required[[, the timeframe is {term}]].',
    },
  },
  {
    id: 'gift2',
    group: 'value',
    media: 'file',
    button: { label: { ru: 'Забрать', en: 'Get it' }, target: 'buy' },
    title: { ru: 'Второй подарок', en: 'Second gift' },
    sub: { ru: 'ещё один бонус в середине серии', en: 'one more bonus in the middle of the series' },
    text: {
      ru: 'Небольшое дополнение к подарку ({gift}) — во вложении. Пользуйтесь.',
      en: 'A small addition to the gift ({gift}) is attached. Please use it.',
    },
  },
  {
    id: 'giftReminder',
    group: 'value',
    media: 'text',
    button: { label: { ru: 'Открыть подарок', en: 'Open the gift' }, target: 'buy' },
    title: { ru: 'Напоминание о подарке', en: 'Gift reminder' },
    sub: { ru: 'как пользоваться первым подарком', en: 'how to use the first gift' },
    text: {
      ru: 'Вы уже открыли подарок — {gift}? Если нет, рекомендую начать сегодня: первая часть занимает около десяти минут.',
      en: 'Have you already opened the gift — {gift}? If not, I recommend starting today: the first part takes about ten minutes.',
    },
  },
  // ── Боль и будущее
  {
    id: 'whyNot',
    group: 'pain',
    media: 'text',
    button: { label: { ru: 'Узнать как', en: 'See how' }, target: 'buy' },
    title: { ru: 'Почему не получается самостоятельно', en: 'Why it does not work on your own' },
    sub: { ru: 'что на самом деле мешает', en: 'what really gets in the way' },
    text: {
      ru: 'Если вы уже пытались[[ {result}]] и оставили попытки, причина, скорее всего, не в вас. [[Чаще всего причина в том, что вы {evenIf}, — и это устранимо.]] Расскажу, как.',
      en: 'If you have already tried[[ {result}]] and gave up, the reason is most likely not you. [[Most often the reason is that you {evenIf}, and this can be fixed.]] I will explain how.',
    },
  },
  {
    id: 'yearLater',
    group: 'pain',
    media: 'text',
    button: { label: { ru: 'Начать сейчас', en: 'Start now' }, target: 'buy' },
    title: { ru: 'Через год', en: 'A year from now' },
    sub: { ru: 'что будет, если ничего не менять', en: 'what happens if nothing changes' },
    text: {
      ru: 'Представьте: прошёл год, и всё осталось как сейчас. Теперь представьте другой год[[: вам удалось {result}]]. Разница — в одном решении, и его можно принять сегодня.',
      en: 'Imagine: a year has passed and everything is as it is now. Now imagine a different year[[: you managed {result}]]. The difference is one decision, and it can be made today.',
    },
  },
  // ── Вовлечение
  {
    id: 'question',
    group: 'engage',
    media: 'text',
    button: { label: { ru: 'Ответить', en: 'Reply' }, target: 'buy' },
    title: { ru: 'Вопрос', en: 'Question' },
    sub: { ru: 'простой выбор: что вам ближе', en: 'a simple choice: what is closer to you' },
    text: {
      ru: 'Ответьте одним словом: что мешает больше всего — время, деньги или сомнение, что получится? Я отвечу лично.',
      en: 'Reply with one word: what gets in the way most — time, money or doubt that it will work? I will reply personally.',
    },
  },
  {
    id: 'quiz',
    group: 'engage',
    media: 'text',
    button: { label: { ru: 'Пройти тест', en: 'Take the test' }, target: 'buy' },
    title: { ru: 'Мини-тест', en: 'Mini quiz' },
    sub: { ru: 'три вопроса о себе', en: 'three questions about yourself' },
    text: {
      ru: 'Три вопроса, чтобы понять, подходит ли вам {productName}: 1) … 2) … 3) … Если вы ответили «да» хотя бы дважды, вам стоит продолжить.',
      en: 'Three questions to understand whether {productName} suits you: 1) … 2) … 3) … If you answered “yes” at least twice, it is worth continuing.',
    },
  },
  {
    id: 'ask',
    group: 'engage',
    media: 'text',
    button: { label: { ru: 'Написать', en: 'Write to me' }, target: 'buy' },
    title: { ru: 'Приглашение написать', en: 'Invitation to write' },
    sub: { ru: 'задайте вопрос', en: 'ask a question' },
    text: {
      ru: 'Если остались вопросы, напишите их в ответ на это сообщение. Я прочитаю и отвечу.',
      en: 'If you have any questions, write them in reply to this message. I will read them and reply.',
    },
  },
  // ── События
  {
    id: 'webinar',
    group: 'events',
    media: 'photo',
    button: { label: BTN.signup, target: 'event' },
    title: { ru: 'Приглашение на вебинар', en: 'Webinar invitation' },
    sub: { ru: 'вебинар, эфир, мастер-класс', en: 'webinar, live stream, master class' },
    text: {
      ru: '{eventDate} состоится бесплатный вебинар[[: как {result}]][[ за {term}]]. Разберём основные ошибки и систему целиком. Количество мест ограничено.',
      en: 'A free webinar will take place on {eventDate}[[: how {result}]][[ in {term}]]. We will go through the main mistakes and the whole system. The number of places is limited.',
    },
  },
  {
    id: 'eventReminder',
    group: 'events',
    media: 'text',
    button: { label: BTN.open, target: 'event' },
    title: { ru: 'Напоминание о событии', en: 'Event reminder' },
    sub: { ru: 'за день до события', en: 'a day before the event' },
    text: {
      ru: 'Напоминаю: завтра в {eventTime} вебинар. Ссылка — по кнопке. Приходите с вопросами.',
      en: 'A reminder: the webinar is tomorrow at {eventTime}. The link is in the button. Come with your questions.',
    },
  },
  {
    id: 'call',
    group: 'events',
    media: 'text',
    button: { label: BTN.signup, target: 'buy' },
    title: { ru: 'Запись на разбор', en: 'Booking a review' },
    sub: { ru: 'бесплатная консультация', en: 'a free consultation' },
    text: {
      ru: 'Предлагаю разобрать вашу ситуацию лично: бесплатный разбор на 20 минут. Нажмите кнопку и выберите удобное время.',
      en: 'I suggest reviewing your situation personally: a free 20-minute review. Press the button and choose a convenient time.',
    },
  },
  // ── Продажа
  // QUICK-START §11.35 ч.1 п.2 (владелец 30.10, текст утверждён 18:05): единственное сообщение цепочки «Продажи» — предложение.
  {
    id: 'offerMsg',
    group: 'sale',
    media: 'text',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Предложение', en: 'Offer' },
    sub: { ru: 'ваш оффер и кнопка «Купить»', en: 'your offer and the “Buy” button' },
    text: {
      ru: 'Здравствуйте. {offer} Подробности и оплата — по кнопке.',
      en: 'Hello. {offer} Details and payment are in the button.',
    },
  },
  {
    id: 'present',
    group: 'sale',
    media: 'photo',
    button: { label: BTN.more, target: 'buy' },
    title: { ru: 'Презентация продукта', en: 'Product presentation' },
    sub: { ru: 'что внутри, формат, для кого', en: 'what is inside, format, who it is for' },
    text: {
      ru: 'Расскажу подробнее о продукте. Это {product}. [[Результат: вы сможете {result}[[ за {term}]].]] [[Как устроено: {how}.]] Для кого: … Подробности — по кнопке.',
      en: 'Let me tell you more about the product. This is the {product}. [[The result: you will be able {result}[[ in {term}]].]] [[How it works: {how}.]] Who it is for: … Details are in the button.',
    },
  },
  {
    id: 'program',
    group: 'sale',
    media: 'file',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Программа файлом', en: 'Programme as a file' },
    sub: { ru: 'PDF с программой', en: 'a PDF with the programme' },
    text: {
      ru: 'Полная программа ({productName}) — во вложении. Ознакомьтесь с содержанием по шагам.',
      en: 'The full programme ({productName}) is attached. Review the content step by step.',
    },
  },
  {
    id: 'price',
    group: 'sale',
    media: 'text',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Цена и состав', en: 'Price and contents' },
    sub: { ru: 'что входит и сколько стоит', en: 'what is included and how much it costs' },
    text: {
      ru: 'Что входит в {productName}: … Стоимость: … Оплата — по кнопке.',
      en: 'What {productName} includes: … Price: … Payment is in the button.',
    },
  },
  {
    id: 'bonus',
    group: 'sale',
    media: 'text',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Бонус за быстрое решение', en: 'Bonus for a quick decision' },
    sub: { ru: 'подарок первым', en: 'a gift for the first ones' },
    text: {
      ru: 'Для тех, кто присоединится в ближайшие два дня, предусмотрен бонус: … После этого срока — только основной пакет.',
      en: 'Those who join within the next two days will receive a bonus: … After that, only the main package is available.',
    },
  },
  {
    id: 'objections',
    group: 'sale',
    media: 'text',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Ответы на возражения', en: 'Answers to objections' },
    sub: { ru: 'дорого, нет времени, не получится', en: 'too expensive, no time, it will not work' },
    text: {
      ru: 'Отвечу на то, что обычно останавливает. «Нет времени»: потребуется … в неделю. «Не получится»: [[даже если вы {evenIf}, ]]результат достигается. «Дорого»: сравните с ценой ещё одного года без результата.',
      en: 'Let me answer what usually holds people back. “No time”: it takes … a week. “It will not work”: [[even if you {evenIf}, ]]the result is achievable. “Too expensive”: compare it with the cost of another year without a result.',
    },
  },
  {
    id: 'guarantee',
    group: 'sale',
    media: 'text',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Гарантия', en: 'Guarantee' },
    sub: { ru: 'снятие риска', en: 'removing the risk' },
    text: {
      ru: 'Если в первые … дней вы поймёте, что это не подходит, деньги будут возвращены без дополнительных вопросов.',
      en: 'If within the first … days you realise it does not suit you, the money will be returned without further questions.',
    },
  },
  {
    id: 'deadline',
    group: 'sale',
    media: 'text',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Дедлайн', en: 'Deadline' },
    sub: { ru: 'мест или времени осталось мало', en: 'few places or little time left' },
    text: {
      ru: 'Набор закрывается {deadline}. Мест осталось немного. Если решение принято, не откладывайте.',
      en: 'Enrolment closes {deadline}. Few places are left. If you have made a decision, do not postpone it.',
    },
  },
  {
    id: 'lastDay',
    group: 'sale',
    media: 'text',
    button: { label: BTN.buy, target: 'buy' },
    title: { ru: 'Последний день', en: 'Last day' },
    sub: { ru: 'последнее напоминание', en: 'the last reminder' },
    text: {
      ru: 'Сегодня последний день набора. После {deadline} ссылка перестанет работать. Это последнее напоминание — далее сообщений о наборе не будет.',
      en: 'Today is the last day of enrolment. After {deadline} the link will stop working. This is the last reminder — there will be no further messages about enrolment.',
    },
  },
  {
    id: 'afterDeadline',
    group: 'sale',
    media: 'text',
    button: { label: BTN.wait, target: 'buy' },
    title: { ru: 'После дедлайна', en: 'After the deadline' },
    sub: { ru: 'не успели — следующий поток', en: 'missed it — the next intake' },
    text: {
      ru: 'Набор закрыт. Если вы не успели, оставьте заявку в лист ожидания — я сообщу, когда откроется следующий.',
      en: 'Enrolment is closed. If you missed it, join the waitlist — I will let you know when the next one opens.',
    },
  },
  // ── Возврат
  {
    id: 'stillRelevant',
    group: 'return',
    media: 'text',
    button: { label: { ru: 'Да, актуально', en: 'Yes, still relevant' }, target: 'buy' },
    title: { ru: 'Ещё актуально?', en: 'Still relevant?' },
    sub: { ru: 'тем, кто давно не отвечал', en: 'for those who have not replied for a while' },
    text: {
      ru: 'Давно не было сообщений от меня. [[Задача «{result}» по-прежнему актуальна для вас?]] Если да, подскажу, с чего начать сейчас. Если нет, оставьте это сообщение без ответа — больше я не напишу.',
      en: 'It has been a while since my last message. [[Is the goal “{result}” still relevant for you?]] If so, I will suggest where to start now. If not, leave this message unanswered — I will not write again.',
    },
  },
];

/** Пять рекомендованных (§2.7, состав — «Решил сам» ведущего, утверждено владельцем «пять»). */
export const QS_RECOMMENDED: readonly string[] = ['intro', 'case', 'lesson', 'present', 'deadline'];

/** Типы, которые всегда в конце серии (§2.9). */
export const QS_ALWAYS_LAST: readonly string[] = ['afterDeadline', 'stillRelevant'];

export function qsType(id: string): QsTypeDef | undefined {
  return QS_TYPES.find((t) => t.id === id);
}

/** Слова медиа для строки D17 («фото · кнопка «Записаться»»). */
/** Слово «кнопка» строки письма («текст · кнопка «Подробнее»», слова владельца §11.34 п.12а). */
export const QS_BUTTON_WORD: QsText = { ru: 'кнопка', en: 'button' };

export const QS_MEDIA_WORDS: Record<QsMedia, QsText> = {
  text: { ru: 'текст', en: 'text' },
  photo: { ru: 'фото', en: 'photo' },
  video: { ru: 'видео', en: 'video' },
  circle: { ru: 'кружок', en: 'video note' },
  audio: { ru: 'аудио', en: 'audio' },
  file: { ru: 'файл', en: 'file' },
};

/**
 * Подсказка окна «Сообщение», пока вложения нет (§2.8a). Образец ТЗ — у кружка; остальные виды — тем же строем
 * («Решил сам»: формулировка по виду медиа, род согласован с существительным).
 */
export const QS_MEDIA_HINTS: Record<Exclude<QsMedia, 'text'>, QsText> = {
  circle: {
    ru: 'Для этого сообщения предусмотрен видеокружок с одной мыслью. Запишите его позже или выберите другой тип вложения на вкладках ниже.',
    en: 'A video note with one thought is intended for this message. Record it later or choose another attachment type on the tabs below.',
  },
  photo: {
    ru: 'Для этого сообщения предусмотрено фото. Добавьте его позже или выберите другой тип вложения на вкладках ниже.',
    en: 'A photo is intended for this message. Add it later or choose another attachment type on the tabs below.',
  },
  video: {
    ru: 'Для этого сообщения предусмотрено видео. Добавьте его позже или выберите другой тип вложения на вкладках ниже.',
    en: 'A video is intended for this message. Add it later or choose another attachment type on the tabs below.',
  },
  audio: {
    ru: 'Для этого сообщения предусмотрена аудиозапись. Добавьте её позже или выберите другой тип вложения на вкладках ниже.',
    en: 'An audio recording is intended for this message. Add it later or choose another attachment type on the tabs below.',
  },
  file: {
    ru: 'Для этого сообщения предусмотрен файл. Добавьте его позже или выберите другой тип вложения на вкладках ниже.',
    en: 'A file is intended for this message. Add it later or choose another attachment type on the tabs below.',
  },
};

/** Фраза вместо кнопки, когда ссылки нет (§3.4 конец). */
export const QS_NO_LINK_PHRASE: QsText = {
  ru: 'Напишите мне в ответ, и я расскажу, как присоединиться.',
  en: 'Reply to me, and I will tell you how to join.',
};

/** Подпись под текстом с многоточием (§3.0). */
export const QS_FILL_DOTS: QsText = { ru: 'Дополните места с многоточием', en: 'Fill in the places marked with an ellipsis' };

/** Метка «купил» (§2.9, §4.1 п.6): заводится при создании серии; есть у человека — серия у него останавливается. */
export const QS_BOUGHT_TAG: QsText = { ru: 'купил', en: 'purchased' };
